import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseConfig';
import { isDemoMode } from '../utils/demoMode';

export interface CustomUser {
  uid: string;
  email: string | null;
  role?: 'artist' | 'piercer' | 'studio' | null;
  studioId?: string | null;
  photoURL?: string | null;
  isMasterStudio?: boolean;
  displayName?: string | null;
  phoneNumber?: string | null;
}

export interface AuthContextType {
  user: CustomUser | null;
  userRole: 'artist' | 'studio' | null;
  subscriptionStatus: string | null;
  studioId: string | null;
  artistId: string | null;
  loading: boolean;
  isMasterStudio: boolean;
  profileReady: boolean;            // NEW

}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEMO_STUDIO_ID = 'demo-studio';
const DEMO_OWNER_UID = 'demo-owner';
const DEMO_EMAIL =
  process.env.REACT_APP_DEMO_LOGIN_EMAIL || 'demo@tattoostudioassistant.com';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CustomUser | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'artist' | 'studio' | null>(null);
  const [isMasterStudio, setIsMasterStudio] = useState(false);
  const [studioId, setStudioId] = useState<string | null>(null);
  const [artistId, setArtistId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(false); // NEW

  const applyDemoContextFallback = React.useCallback(() => {
    setSubscriptionStatus('active');
    setStudioId(DEMO_STUDIO_ID);
    setArtistId(null);
    setUserRole('studio');
    setIsMasterStudio(false);
    setUser({
      uid: auth.currentUser?.uid || DEMO_OWNER_UID,
      email: auth.currentUser?.email || DEMO_EMAIL,
      role: 'studio',
      studioId: DEMO_STUDIO_ID,
      isMasterStudio: false,
      photoURL: auth.currentUser?.photoURL ?? null,
      displayName: auth.currentUser?.displayName || 'Demo Studio Owner',
      phoneNumber: auth.currentUser?.phoneNumber ?? null,
    });
    setProfileReady(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isDemoMode) {
      applyDemoContextFallback();
      return;
    }

    let unsubscribeUserDoc: (() => void) | null = null;
    let profileFallbackTimer: number | null = null;
    let demoFallbackTimer: number | null = null;
    let demoFallbackApplied = false;

    const clearProfileFallbackTimer = () => {
      if (profileFallbackTimer != null) {
        window.clearTimeout(profileFallbackTimer);
        profileFallbackTimer = null;
      }
    };

    const clearDemoFallbackTimer = () => {
      if (demoFallbackTimer != null) {
        window.clearTimeout(demoFallbackTimer);
        demoFallbackTimer = null;
      }
    };

    const applyDemoFallbackOnce = () => {
      if (!isDemoMode || demoFallbackApplied) return;
      demoFallbackApplied = true;
      clearDemoFallbackTimer();
      applyDemoContextFallback();
    };

    if (isDemoMode) {
      demoFallbackTimer = window.setTimeout(applyDemoFallbackOnce, 2500);
    }

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser: User | null) => {
      if (isDemoMode && demoFallbackApplied && !firebaseUser) {
        return;
      }

      clearProfileFallbackTimer();
      setLoading(true);
      setProfileReady(false);              // NEW reset per auth change
      setUser(null);
      setSubscriptionStatus('unknown'); 
      setStudioId(null);
      setArtistId(null);
      setUserRole(null);
      setIsMasterStudio(false);

      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }

      if (firebaseUser) {
        if (isDemoMode) {
          applyDemoFallbackOnce();
          return;
        }

        const uid = firebaseUser.uid;
        let profileResolved = false;
        let fallbackApplied = false;

        const applyClaimsFallback = async () => {
          if (profileResolved || fallbackApplied) return;
          fallbackApplied = true;

          try {
            const idToken = await firebaseUser.getIdTokenResult(true);
            const claims = (idToken.claims || {}) as any;
            const roleClaim = (claims.role ?? null) as 'artist' | 'piercer' | 'studio' | null;
            const effectiveRole = roleClaim === 'piercer' ? 'artist' : roleClaim;
            const claimedStudioIds = Array.isArray(claims.studioIds)
              ? claims.studioIds.map((id: unknown) => String(id || '').trim()).filter(Boolean)
              : [];
            const claimedStudioId =
              typeof claims.studioId === 'string' && claims.studioId.trim()
                ? claims.studioId.trim()
                : claimedStudioIds[0] ?? null;
            const master = claims.isMasterStudio === true;

            setSubscriptionStatus('unknown');
            setStudioId(claimedStudioId);
            setArtistId(effectiveRole === 'artist' ? uid : null);
            setUserRole(effectiveRole);
            setIsMasterStudio(master);
            setUser({
              uid,
              email: firebaseUser.email,
              role: roleClaim,
              studioId: claimedStudioId,
              isMasterStudio: master,
              photoURL: firebaseUser.photoURL ?? null,
              displayName: firebaseUser.displayName ?? null,
              phoneNumber: firebaseUser.phoneNumber ?? null,
            });
          } catch (error) {
            console.error('Auth claims fallback failed:', error);
            setSubscriptionStatus('unknown');
            setUser({
              uid,
              email: firebaseUser.email,
              photoURL: firebaseUser.photoURL ?? null,
              displayName: firebaseUser.displayName ?? null,
              phoneNumber: firebaseUser.phoneNumber ?? null,
            });
          } finally {
            setProfileReady(true);
            setLoading(false);
          }
        };

        const startUserDocListener = async () => {
          // Prime token in background, but don't block user doc listener startup.
          void firebaseUser.getIdToken().catch(() => {});
          profileFallbackTimer = window.setTimeout(() => {
            void applyClaimsFallback();
          }, 6000);

          const userDocRef = doc(db, 'users', uid);
          unsubscribeUserDoc = onSnapshot(
            userDocRef,
            (docSnap) => {
              if (profileResolved) return;
              profileResolved = true;
              clearProfileFallbackTimer();
              if (docSnap.exists()) {
                const userData = docSnap.data() as any;
                const rawRole = typeof userData.role === 'string' ? userData.role.trim().toLowerCase() : null;
                const rolesArray = Array.isArray(userData.roles) ? userData.roles : [];
                const hasStudioRoleInArray = rolesArray.some(
                  (r: unknown) => String(r).trim().toLowerCase() === 'studio'
                );
                const normalizedRole: 'artist' | 'piercer' | 'studio' | null =
                  rawRole === 'artist' || rawRole === 'piercer'
                    ? rawRole
                    : rawRole === 'studio' || rawRole === 'pending_studio_owner' || hasStudioRoleInArray
                    ? 'studio'
                    : null;
                const effectiveRole = normalizedRole === 'piercer' ? 'artist' : normalizedRole;

                const firstStudioIdFromArray = Array.isArray(userData.studioIds)
                  ? userData.studioIds
                      .map((id: unknown) => String(id || '').trim())
                      .find(Boolean) || null
                  : null;
                const sId =
                  typeof userData.studioId === 'string' && userData.studioId.trim()
                    ? userData.studioId.trim()
                    : firstStudioIdFromArray;

                const master = userData.isMasterStudio === true;

                setSubscriptionStatus(userData.subscription?.status || 'incomplete');
                setStudioId(sId);
                setArtistId(normalizedRole === 'artist' || normalizedRole === 'piercer' ? uid : null);
                setUserRole(effectiveRole);
                setIsMasterStudio(master);

                setUser({
                  uid,
                  email: firebaseUser.email,
                  role: normalizedRole,
                  studioId: sId,
                  isMasterStudio: master,
                  photoURL: firebaseUser.photoURL ?? null,
                  displayName: firebaseUser.displayName ?? null,
                  phoneNumber: firebaseUser.phoneNumber ?? null,
                });
              } else {
                setSubscriptionStatus('incomplete');
                setStudioId(null);
                setArtistId(null);
                setUserRole(null);
                setIsMasterStudio(false);
                setUser({
                  uid,
                  email: firebaseUser.email,
                  photoURL: firebaseUser.photoURL ?? null,
                  displayName: firebaseUser.displayName ?? null,
                  phoneNumber: firebaseUser.phoneNumber ?? null,
                });
              }
              setProfileReady(true);
              setLoading(false);
            },
            async (error) => {
              console.error('Firestore snapshot listener failed:', error);
              clearProfileFallbackTimer();
              await applyClaimsFallback();
            }
          );
        };

        void startUserDocListener();
      } else {
        setProfileReady(true);
        setLoading(false);
      }
    });

    return () => {
      clearProfileFallbackTimer();
      clearDemoFallbackTimer();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
      unsubscribeAuth();
    };
  }, [applyDemoContextFallback]);

const value = React.useMemo(
  () => ({
    user, userRole, subscriptionStatus, loading, studioId, artistId, isMasterStudio, profileReady,
  }),
  // ❗ profileReady MUST be included here so consumers get the update
  [user, userRole, subscriptionStatus, loading, studioId, artistId, isMasterStudio, profileReady]
);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
